import React, { useState, useMemo, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Box, 
  Typography, 
  List, 
  ListItem, 
  ListItemText, 
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Switch
} from '@mui/material';
import UserSelectionModal from '../../components/UserSelectionModal';
import ModalConfirm from '../../components/ModalConfirm';
import { useAdmin } from '../../contexts/AdminContext';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import getConfig from '../../config/app-config';

const ModalPayment = ({ open, onClose, onPaymentCreated }) => {
  const { addPayment, enrollments } = useAdmin();
  const config = getConfig();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [paymentType, setPaymentType] = useState('cash');
  const [useCustomAmount, setUseCustomAmount] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [note, setNote] = useState('');

  const activeEnrollments = useMemo(
    () => enrollments.filter((e) => e.status !== 'suspended'),
    [enrollments]
  );

  // Utenti unici membri dagli enrollments
  const users = useMemo(() => {
    const map = new Map();
    activeEnrollments.forEach(e => {
  
     
        if (!map.has(e.userId)) {
          if (e.user) {
            e.user.id = e.userId;
            map.set(e.userId, e.user);
          }
        }
      
    });

    return Array.from(map.values());
  }, [activeEnrollments]);

  // Iscrizioni dell'utente selezionato
  const userEnrollments = useMemo(() => {
    if (!selectedUser) return [];
  
    return activeEnrollments.filter(e => e.userId === selectedUser.id);
  }, [selectedUser, activeEnrollments]);

  useEffect(() => {
    if (selectedEnrollment) {
      const stillActive = activeEnrollments.find((e) => e._id === selectedEnrollment._id);
      if (!stillActive) {
        setSelectedEnrollment(null);
      } else if (stillActive !== selectedEnrollment) {
        setSelectedEnrollment(stillActive);
      }
    }
  }, [selectedEnrollment, activeEnrollments]);

  useEffect(() => {
    if (selectedUser) {
      const hasActive = activeEnrollments.some((e) => e.userId === selectedUser.id);
      if (!hasActive) {
        setSelectedUser(null);
        setSelectedEnrollment(null);
      }
    }
  }, [selectedUser, activeEnrollments]);

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setSelectedEnrollment(null);
  };

  const handleEnrollmentSelect = (enrollment) => {
    setSelectedEnrollment(enrollment);
  };

  const handleRegisterPayment = async () => {
    setLoading(true);
    setError('');
    try {
      const amount = useCustomAmount && customAmount ? parseFloat(customAmount) : selectedEnrollment.amount;
      
      const result = await addPayment({
        enrollmentId: selectedEnrollment._id,
        userId: selectedUser.id,
        amount: amount,
        paymentType: paymentType,
        customAmount: useCustomAmount && customAmount ? parseFloat(customAmount) : null,
        note: note
      });
      
      if (result.success) {
        if (onPaymentCreated) onPaymentCreated(result.data);
        setSuccess(true);
      } else {
        console.log('result', result);
        setError(result.error || 'Errore nella registrazione pagamento');
      }
    } catch (e) {
      setError('Errore nella registrazione pagamento');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedUser(null);
    setSelectedEnrollment(null);
    setError('');
    setSuccess(false);
    setPaymentType('cash');
    setUseCustomAmount(false);
    setCustomAmount('');
    setNote('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Registra Pagamento</DialogTitle>
      <DialogContent>
        {!selectedUser && (
          <Button variant="outlined" onClick={() => setUserModalOpen(true)} sx={{ mb: 2 }}>
            Seleziona Utente
          </Button>
        )}
        {selectedUser && (
          <Box sx={{ p: 2, mb: 2, borderColor: 'grey.300', borderWidth: 1, borderStyle: 'solid', borderRadius: 2, display: 'flex', alignItems: 'center', width: '100%', minHeight: 56 }}>
            <Typography variant='body1' fontWeight='bold' noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis', flexGrow: 1 }}>
              {selectedUser.name} {selectedUser.surname}
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ ml: 2 }}>
              {selectedUser.email}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
              <Tooltip title='Deseleziona utente'>
                <IconButton aria-label='Deseleziona utente' color='error' onClick={() => { setSelectedUser(null); setSelectedEnrollment(null); }}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title='Cambia utente'>
                <IconButton aria-label='Cambia utente' color='primary' onClick={() => { setUserModalOpen(true); setSelectedEnrollment(null); }}>
                  <PersonSearchIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
        {selectedUser && !selectedEnrollment && (
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Seleziona iscrizione:</Typography>
            <List>
              {userEnrollments.map(e => (
                <ListItem key={e._id} sx={{ py: 0.5, minHeight: 36 }}
                  secondaryAction={
                    <Button variant="contained" size="small" sx={{ minWidth: 0, px: 1.5, py: 0.5, fontSize: '0.85rem' }} onClick={() => handleEnrollmentSelect(e)}>
                      Seleziona
                    </Button>
                  }
                >
                  <ListItemText
                    primary={<span style={{ fontSize: '1rem' }}>{e.course?.name || 'Corso'}</span>}
                    secondary={<span style={{ fontSize: '0.85rem', color: '#888' }}>{`Importo: €${e.amount}`}</span>}
                    sx={{ my: 0 }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        {selectedEnrollment && (
          <Box sx={{ p: 2, mb: 2, borderColor: 'grey.300', borderWidth: 1, borderStyle: 'solid', borderRadius: 2, display: 'flex', alignItems: 'center', width: '100%', minHeight: 56 }}>
            <Typography variant='body1' fontWeight='bold' noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis', flexGrow: 1 }}>
              {selectedEnrollment.course?.name}
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ ml: 2 }}>
              Importo: €{useCustomAmount && customAmount ? customAmount : selectedEnrollment.amount}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
              <Tooltip title='Deseleziona iscrizione'>
                <IconButton aria-label='Deseleziona iscrizione' color='error' onClick={() => setSelectedEnrollment(null)}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title='Cambia iscrizione'>
                <IconButton aria-label='Cambia iscrizione' color='primary' onClick={() => setSelectedEnrollment(null)}>
                  <PersonSearchIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
        {selectedEnrollment && (
          <>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="payment-type-label">Tipo di Pagamento</InputLabel>
              <Select
                labelId="payment-type-label"
                id="payment-type-select"
                value={paymentType}
                label="Tipo di Pagamento"
                onChange={(e) => setPaymentType(e.target.value)}
                disabled={loading || success}
              >
                <MenuItem value="cash">Contanti</MenuItem>
                <MenuItem value="bank_transfer">Bonifico</MenuItem>
              </Select>
            </FormControl>
            
            {config.USE_CUSTOM_AMOUNT && (
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useCustomAmount}
                      onChange={(e) => {
                        setUseCustomAmount(e.target.checked);
                        if (!e.target.checked) {
                          setCustomAmount('');
                        }
                      }}
                      disabled={loading || success}
                    />
                  }
                  label="Usa importo personalizzato"
                />
                
                {useCustomAmount && (
                  <TextField
                    fullWidth
                    label="Importo personalizzato (€)"
                    type="number"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    disabled={loading || success}
                    sx={{ mt: 1 }}
                    inputProps={{ min: 0, step: 0.01 }}
                    helperText={`Importo originale: €${selectedEnrollment.amount}`}
                  />
                )}
              </Box>
            )}
            
            {config.WITH_PAYMENT_NOTES && (
              <TextField
                fullWidth
                label="Note (opzionale)"
                multiline
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={loading || success}
                sx={{ mt: 2 }}
                inputProps={{ maxLength: 500 }}
                helperText={`${note.length}/500 caratteri`}
              />
            )}
          </>
        )}
        {error && <Typography color="error" sx={{ mt: 1 }}>{error}</Typography>}
        {success && <Typography color="success.main" sx={{ mt: 1 }}>Pagamento registrato!</Typography>}
        {loading && <CircularProgress size={24} sx={{ mt: 2 }} />}
      </DialogContent>
      <DialogActions>
        {success ? (
          <Button onClick={handleClose} variant="contained" color="primary">Chiudi</Button>
        ) : (
          <>
            <Button onClick={handleClose}>Annulla</Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!selectedUser || !selectedEnrollment || loading || (config.USE_CUSTOM_AMOUNT && useCustomAmount && !customAmount)}
              onClick={() => setConfirmOpen(true)}
            >
              Registra Pagamento
            </Button>
          </>
        )}
      </DialogActions>
      <UserSelectionModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        onSelect={handleUserSelect}
        users={users}
      />
      <ModalConfirm
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Conferma registrazione pagamento"
        content={`Registrare il pagamento per ${selectedUser?.name} ${selectedUser?.surname} per il corso "${selectedEnrollment?.course?.name}" per un importo di €${useCustomAmount && customAmount ? customAmount : selectedEnrollment?.amount}?`}
        onConfirm={() => {
          setConfirmOpen(false);
          handleRegisterPayment();
        }}
      />
    </Dialog>
  );
};

export default ModalPayment; 